// src/ConsumerApp/api/products/product.hooks.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ProductService } from './product.service';

/**
 * Hook to fetch paginated selling products[cite: 4]
 */
// src/ConsumerApp/api/products/product.hooks.js

export const useGetSellingProducts = (params) => {
  return useQuery({
    queryKey: ['products', params],
    queryFn: async () => {
      const response = await ProductService.getSellingProducts(params);
      return response?.data || response;
    },
    // Only category is required to run the query; customer_id is optional
    enabled: Boolean(params?.product_category_id),
    keepPreviousData: true,
    staleTime: 1000 * 60 * 2,
  });
};

/**
 * Hook to add item to bucket[cite: 4]
 */
export const useAddItemToBucket = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => ProductService.addItemToBucket(payload),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['bucketSummary', variables.customer_id] }),
        queryClient.invalidateQueries({ queryKey: ['bucketDetails', variables.customer_id] }),
      ]);
    },
  });
};

/**
 * Hook to update bucket item quantity[cite: 4]
 */
export const useUpdateBucketItemQuantity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => ProductService.updateBucketItemQuantity(payload),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['bucketSummary', variables.customer_id] }),
        queryClient.invalidateQueries({ queryKey: ['bucketDetails', variables.customer_id] }),
      ]);
    },
  });
};

/**
 * Hook to delete item from bucket
 */
export const useDeleteBucketItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => ProductService.deleteBucketItem(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['bucketSummary'] });
      await queryClient.invalidateQueries({ queryKey: ['bucketDetails'] });
    },
  });
};

/**
 * Hook to fetch bucket summary (counts and total value)[cite: 4]
 */
export const useGetBucketSummary = (customer_id) => {
  return useQuery({
    queryKey: ['bucketSummary', customer_id],
    queryFn: async () => {
      const response = await ProductService.getBucketSummary(customer_id);
      return response?.data || response;
    },
    enabled: Boolean(customer_id),
  });
};

/**
 * Hook to fetch detailed bucket items and bill breakdown[cite: 4]
 */
export const useGetBucketItemsAndBillSummary = ({
  customer_id,
  purchase_type,
  delivery_address_id,
}) => {
  return useQuery({
    queryKey: ['bucketDetails', customer_id, purchase_type, delivery_address_id],
    queryFn: async () => {
      const response = await ProductService.getBucketItemsAndBillSummary({
        customer_id,
        purchase_type,
        delivery_address_id,
      });
      return response?.data || response;
    },
    enabled: Boolean(customer_id && purchase_type),
  });
};

/**
 * Hook to fetch product by ID[cite: 8]
 */
export const useGetProductById = (id) => {
  return useQuery({
    queryKey: ['product', id],
    queryFn: async () => {
      const response = await ProductService.getProductById(id);
      return response?.data || response;
    },
    enabled: Boolean(id),
    staleTime: 1000 * 60 * 5,
  });
};

/**
 * Hook to fetch product variants[cite: 8]
 */
export const useGetProductVariants = () => {
  return useMutation({
    mutationFn: async ({ product_id, purchase_mode }) => {
      const response = await ProductService.getProductVariants(product_id, purchase_mode);
      return response?.data || response;
    },
  });
};

/**
 * Hook to fetch FAQs by subcategory ID[cite: 8]
 */
export const useGetFaqsBySubCategoryId = (subcategoryId) => {
  return useQuery({
    queryKey: ['faqs', subcategoryId],
    queryFn: async () => {
      const response = await ProductService.getFaqsBySubCategoryId(subcategoryId);
      return response?.data || response || [];
    },
    enabled: Boolean(subcategoryId),
    staleTime: 1000 * 60 * 10,
  });
};

/**
 * Hook to fetch products by category IDs[cite: 8]
 */
export const useGetProductsByCategoryIds = (categoryIds) => {
  return useQuery({
    queryKey: ['productsByCategories', categoryIds],
    queryFn: async () => {
      const response = await ProductService.getProductsByCategoryIds(categoryIds);
      return response?.data || response || [];
    },
    enabled: Array.isArray(categoryIds) && categoryIds.length > 0,
  });
};

/**
 * Hook to fetch category filter attributes
 */
export const useGetFiltersByCategory = (categoryId) => {
  return useQuery({
    queryKey: ['categoryFilters', categoryId],
    queryFn: async () => {
      const response = await ProductService.getFiltersByCategory(categoryId);
      return response?.data || response;
    },
    enabled: Boolean(categoryId),
    staleTime: 1000 * 60 * 10,
  });
};

/**
 * Hook to fetch most searched products
 */
export const useGetMostSearchedProducts = (options = {}) => {
  return useQuery({
    queryKey: ['mostSearchedProducts'],
    queryFn: async () => {
      const response = await ProductService.getMostSearchedProducts();
      return response?.data || response || [];
    },
    staleTime: 1000 * 60 * 5,
    ...options,
  });
};

export const useFrequentlyBoughtProducts = (options = {}) => {
  return useQuery({
    queryKey: ['frequentlyBoughtProducts'],
    queryFn: async () => {
      const response = await ProductService.getFrequentlyBoughtProducts();
      return response?.data || response || [];
    },
    staleTime: 1000 * 60 * 5,
    ...options,
  });
};

export const useGetFeaturedProducts = () => {
  return useQuery({
    queryKey: ['products', 'featured'],
    queryFn: async () => {
      const res = await ProductService.getFeaturedProducts();
      return Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes cache
  });
};

/**
 * Hook to fetch similar products
 */
export const useGetSimilarProducts = ({ customer_id, product_id }, options = {}) => {
  return useQuery({
    queryKey: ['similarProducts', customer_id, product_id],
    queryFn: async () => {
      const response = await ProductService.getSimilarProducts({ customer_id, product_id });
      return response?.data || response || [];
    },
    enabled: Boolean(product_id),
    staleTime: 1000 * 60 * 5,
    ...options,
  });
};

/**
 * Hook to fetch related products
 */
export const useGetRelatedProducts = ({ customer_id, product_id }, options = {}) => {
  return useQuery({
    queryKey: ['relatedProducts', customer_id, product_id],
    queryFn: async () => {
      const response = await ProductService.getRelatedProducts({ customer_id, product_id });
      return response?.data || response || [];
    },
    enabled: Boolean(product_id),
    staleTime: 1000 * 60 * 5,
    ...options,
  });
};